// Pure layer-registry logic (Front-End.md §A7 render branch + §C). No MapLibre here
// so it's fully unit-testable. The MapWindow reconciler consumes these descriptors.
import type { ManifestLayer, CategoryEntry } from "../types/contract";
import { rampStopsFor } from "./cogColor";
import { legendNumericRange } from "./absoluteColorScale";
import { legendCategories } from "./classLegend";

export type RenderMode = "categorical" | "continuous" | "vector";

export interface LayerUiState {
  visible: boolean;
  opacity: number;
  order: number;
}

// THE render branch: a COG row with `categories` => categorical; a COG without => continuous
// geojson/vector => vector.
export function renderModeOf(layer: ManifestLayer): RenderMode {
  if (layer.kind === "geojson" || layer.kind === "vector") return "vector";
  if (layer.categories && layer.categories.length > 0) return "categorical";
  return "continuous";
}

export function defaultUiState(layer: ManifestLayer): LayerUiState {
  // Saved zones are shown by default so a user who created/saved zones SEES them on the map the
  // radio rule still lets them toggle to a data layer; this only sets the initial state.
  const id = layer.id;
  const isTargetDesign =
    id.startsWith("target-points-") || id.startsWith("target-zones-");
  const visible = isTargetDesign
    ? false
    : id === "zones"
      ? true
      : layer.default_visible;
  return { visible, opacity: layer.default_opacity, order: layer.order };
}

export function initUiState(
  layers: ManifestLayer[],
): Record<string, LayerUiState> {
  const out: Record<string, LayerUiState> = {};
  for (const l of layers) out[l.id] = defaultUiState(l);
  return out;
}

// A Visualize open starts blank: every layer off, the field boundary on.
// `default_visible` and the zones exception in `defaultUiState` stay in force
// for a layer that appears later in the same visit (a rec the user just built).
export function openUiState(
  layers: ManifestLayer[],
): Record<string, LayerUiState> {
  const out: Record<string, LayerUiState> = {};
  for (const l of layers) {
    out[l.id] = {
      visible: isBoundaryLayer(l),
      opacity: l.default_opacity,
      order: l.order,
    };
  }
  return out;
}

const SAMPLE_KRIGE_PREFIX = "sample-krige-";

// Stacking order: lower `order` drawn first (under), higher on top. Bottom-up array.
// A sample-krige surface stays under its source sample layer. Re-init keeps a stored
// order, which can sit below the surface's own order.
export function stackOrder(
  layers: ManifestLayer[],
  ui: Record<string, LayerUiState>,
): ManifestLayer[] {
  const rank = new Map(
    layers.map((layer) => [layer.id, ui[layer.id]?.order ?? layer.order]),
  );
  for (const layer of layers) {
    if (!layer.id.startsWith(SAMPLE_KRIGE_PREFIX)) continue;
    const src = rank.get(layer.id.slice(SAMPLE_KRIGE_PREFIX.length));
    const own = rank.get(layer.id);
    if (src == null || own == null || own < src) continue;
    rank.set(layer.id, src - 1);
  }
  return [...layers].sort(
    (a, b) => (rank.get(a.id) ?? 0) - (rank.get(b.id) ?? 0),
  );
}

// Paint order for the MAP z-pass: rasters first (under), then vectors (boundary/zones/
// points) — each group by `order`. Guards against a manifest whose raster `order` exceeds
// the boundary's: opaque imagery must never swallow the field outline / sample points.
export function paintOrder(
  layers: ManifestLayer[],
  ui: Record<string, LayerUiState>,
): ManifestLayer[] {
  const ordered = stackOrder(layers, ui);
  return [
    ...ordered.filter((l) => renderModeOf(l) !== "vector"),
    ...ordered.filter((l) => renderModeOf(l) === "vector"),
  ];
}

// The field boundary is the always-on base outline: a GIVEN — not shown as a toggle and never
// hidden, regardless of ui/manifest-default state.
export const BOUNDARY_LAYER_ID = "boundary";
export function isBoundaryLayer(l: ManifestLayer): boolean {
  if (l.id === BOUNDARY_LAYER_ID) return true;
  if (l.id === "zones" || renderModeOf(l) !== "vector") return false;
  const t = (l.inline as { type?: string } | null | undefined)?.type;
  return t === "Polygon" || t === "MultiPolygon";
}

// Non-boundary vector families that share the manifest with the boundary (sample points, zones
// saved maps, client-computed cell grids). Everything else vector-with-a-source is the boundary.
const NON_BOUNDARY_ID =
  /^(zones|soil-|fert-|cov-|ml-|comp-|clayfn-|kfix|sel-|suggested|point)/;
// The field's boundary layer for DOWNLOAD/SELECTION. `isBoundaryLayer` is only safe for the
// always-visible render rule (it needs the INLINE bare geometry to avoid force-showing cell grids), so
// it MISSES an uploaded/parsed boundary served via `signed_url` with a UUID id. This picks that up:
// the inline/'boundary' row first, else a vector layer with a source whose id isn't a known
// non-boundary family — mirroring `useSelectionBoundaries`/`boundaryLayerOf`, which DO draw it.
export function boundaryLayerFor(
  layers: ManifestLayer[],
): ManifestLayer | undefined {
  return (
    layers.find(isBoundaryLayer) ??
    layers.find(
      (l) =>
        renderModeOf(l) === "vector" &&
        !NON_BOUNDARY_ID.test(l.id ?? "") &&
        (l.inline != null || l.signed_url != null),
    )
  );
}

export function withUnit(name: string, unit?: string | null): string {
  const u = (unit ?? "").trim();
  return u ? `${name} · ${u}` : name;
}

// Every other layer follows its ui visibility (falling back to the manifest default). Single source
// of truth for the render decision, used by MapWindow + visibleLayers.
export function isLayerVisible(
  layer: ManifestLayer,
  ui: Record<string, LayerUiState>,
): boolean {
  if (isBoundaryLayer(layer)) return true;
  return ui[layer.id]?.visible ?? layer.default_visible;
}

export function visibleLayers(
  layers: ManifestLayer[],
  ui: Record<string, LayerUiState>,
): ManifestLayer[] {
  return stackOrder(layers, ui).filter((l) => isLayerVisible(l, ui));
}

// Build a class_value -> color lookup for a categorical layer.
export function classColorMap(layer: ManifestLayer): Record<number, string> {
  const out: Record<number, string> = {};
  for (const c of layer.categories ?? []) out[c.class_value] = c.color;
  return out;
}

export type Legend =
  | { mode: "categorical"; entries: CategoryEntry[] }
  | {
      mode: "continuous";
      min: number;
      max: number;
      unit?: string;
      colormap?: string;
    }
  | { mode: "truecolor" } // per-cell true color (bare-soil color) — no ramp, the color IS the data
  | { mode: "vector" };

export function legendOf(layer: ManifestLayer): Legend {
  // Legend branches on the RENDER METADATA, not the byte format: a gridded covariate
  // (geojson + colormap/categories, drawn as a choropleth) legends like its COG twin.
  if (layer.rgb) return { mode: "truecolor" }; // true-color has no ramp; each cell shows its actual soil color
  if (layer.categories && layer.categories.length > 0)
    return {
      mode: "categorical",
      entries: legendCategories(layer.categories),
    };
  if (
    renderModeOf(layer) === "continuous" ||
    layer.colormap != null ||
    (layer.value_min != null && layer.value_max != null)
  ) {
    const range = legendNumericRange(layer);
    return {
      mode: "continuous",
      min: range.min,
      max: range.max,
      unit: layer.unit,
      colormap: layer.colormap,
    };
  }
  return { mode: "vector" };
}

// A representative color for mock raster rendering (categorical -> first class; continuous ->
// the mid stop of the layer's red→green agronomic ramp — same either direction).
export function representativeColor(layer: ManifestLayer): string {
  const mode = renderModeOf(layer);
  if (mode === "categorical") return layer.categories?.[0]?.color ?? "#888";
  const stops = rampStopsFor(layer);
  return stops[Math.floor(stops.length / 2)];
}
